/**
 * NavigatorX User Actions Component
 * 
 * Dropdown menu or action bar for user management actions:
 * - Edit user
 * - Delete user
 * - Reset password
 * - Force logout
 * - Extend trial
 * - Grant subscription
 * - Suspend/Unsuspend
 * - Send notification
 * - View audit log
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState } from 'react';
import { Button } from '../../ui/button';
import {
  MoreVertical,
  Edit,
  Trash2,
  Key,
  LogOut,
  Clock,
  Crown,
  Ban,
  Bell,
  Shield,
  History,
  Gift,
  Send,
  Copy,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../../utils/firebase/client';
import {
  extendTrial,
  grantSubscription,
  suspendUser,
  unsuspendUser,
  regenerateReferralCode
} from '../../../utils/services/navigatorXService';
import { logAdminAction } from '../../../utils/services/navigatorXAuditService';
import type { NavUser } from '../../../types/navigatorx';

interface UserActionsProps {
  user: NavUser;
  onEdit: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  variant?: 'dropdown' | 'buttons' | 'icons';
}

export default function UserActions({ 
  user, 
  onEdit, 
  onDelete, 
  onRefresh,
  variant = 'dropdown' 
}: UserActionsProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);

  const adminId = auth.currentUser?.uid || 'unknown';

  // Password Reset
  const handlePasswordReset = async () => {
    if (!user.email) {
      toast.error('User has no email address for password reset');
      return;
    }

    setLoading('password');
    try {
      const sendPasswordReset = httpsCallable(functions, 'sendNavigatorPasswordReset');
      await sendPasswordReset({ email: user.email });
      
      await logAdminAction(adminId, 'SEND_EMAIL', user.id, {
        type: 'password_reset',
        email: user.email
      });

      toast.success(`Password reset email sent to ${user.email}`);
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast.error(error.message || 'Failed to send password reset');
    } finally {
      setLoading(null);
      setShowMenu(false);
    }
  };

  // Force Logout (invalidate sessions)
  const handleForceLogout = async () => {
    setLoading('logout');
    try {
      const forceLogout = httpsCallable(functions, 'forceNavigatorUserLogout');
      await forceLogout({ userId: user.id });
      
      await logAdminAction(adminId, 'UPDATE_USER', user.id, {
        action: 'force_logout'
      });

      toast.success(`${user.displayName || 'User'} has been logged out of all devices`);
    } catch (error: any) {
      console.error('Force logout error:', error);
      toast.error(error.message || 'Failed to force logout');
    } finally {
      setLoading(null);
      setShowMenu(false);
    }
  };

  // Extend Trial
  const handleExtendTrial = async (days: number) => {
    setLoading(`trial-${days}`);
    try {
      await extendTrial(user.id, days, adminId);
      toast.success(`Extended trial by ${days} days`);
      onRefresh();
    } catch (error: any) {
      console.error('Extend trial error:', error);
      toast.error(error.message || 'Failed to extend trial');
    } finally {
      setLoading(null);
      setShowMenu(false);
    }
  };

  // Grant Subscription
  const handleGrantSubscription = async (months: number) => {
    setLoading(`sub-${months}`);
    try {
      await grantSubscription(user.id, months, adminId, 'Admin granted');
      toast.success(`Granted ${months} month subscription`);
      onRefresh();
    } catch (error: any) {
      console.error('Grant subscription error:', error);
      toast.error(error.message || 'Failed to grant subscription');
    } finally {
      setLoading(null);
      setShowMenu(false);
    }
  };

  // Suspend/Unsuspend
  const handleToggleSuspend = async () => {
    const isSuspended = user.subscriptionStatus === 'BLOCKED';
    setLoading('suspend');
    
    try {
      if (isSuspended) {
        await unsuspendUser(user.id, adminId);
        toast.success(`${user.displayName || 'User'} has been unsuspended`);
      } else {
        await suspendUser(user.id, 'Admin suspended', adminId);
        toast.success(`${user.displayName || 'User'} has been suspended`);
      }
      onRefresh();
    } catch (error: any) {
      console.error('Suspend toggle error:', error);
      toast.error(error.message || 'Failed to update suspension status');
    } finally {
      setLoading(null);
      setShowMenu(false);
    }
  };

  // Regenerate Referral Code
  const handleRegenerateCode = async () => {
    setLoading('referral');
    try {
      const newCode = await regenerateReferralCode(user.id, adminId);
      toast.success(`New referral code: ${newCode}`);
      onRefresh();
    } catch (error: any) {
      console.error('Regenerate code error:', error);
      toast.error(error.message || 'Failed to regenerate code');
    } finally {
      setLoading(null);
      setShowMenu(false);
    }
  };

  // Copy Referral Code
  const handleCopyCode = () => {
    if (user.referralCode) {
      navigator.clipboard.writeText(user.referralCode);
      toast.success('Referral code copied to clipboard');
    }
  };

  // Send Notification
  const handleSendNotification = () => {
    toast.info('Notification panel coming soon');
    setShowMenu(false);
  };

  // View Activity
  const handleViewActivity = () => {
    toast.info('Activity timeline coming soon');
    setShowMenu(false);
  };

  const isSuspended = user.subscriptionStatus === 'BLOCKED';

  // Render as dropdown menu
  if (variant === 'dropdown') {
    return (
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowMenu(!showMenu)}
          className="h-8 w-8 p-0"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>

        {showMenu && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowMenu(false)}
            />
            
            {/* Menu */}
            <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border z-50">
              <div className="p-1">
                {/* Edit */}
                <button
                  onClick={() => { onEdit(); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  <Edit className="h-4 w-4" />
                  Edit User
                </button>

                {/* Password Reset */}
                {user.email && (
                  <button
                    onClick={handlePasswordReset}
                    disabled={loading === 'password'}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                  >
                    {loading === 'password' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Key className="h-4 w-4" />
                    )}
                    Reset Password
                  </button>
                )}

                {/* Force Logout */}
                <button
                  onClick={handleForceLogout}
                  disabled={loading === 'logout'}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  {loading === 'logout' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogOut className="h-4 w-4" />
                  )}
                  Force Logout
                </button>

                <div className="border-t my-1" />

                {/* Trial Extensions */}
                <div className="px-3 py-1 text-xs text-gray-400 font-medium">
                  Extend Trial
                </div>
                <div className="flex gap-1 px-3 pb-2">
                  {[7, 14, 30].map(days => (
                    <button
                      key={days}
                      onClick={() => handleExtendTrial(days)}
                      disabled={loading === `trial-${days}`}
                      className="flex-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50"
                    >
                      {loading === `trial-${days}` ? '...' : `+${days}d`}
                    </button>
                  ))}
                </div>

                {/* Subscription Grants */}
                <div className="px-3 py-1 text-xs text-gray-400 font-medium">
                  Grant Subscription
                </div>
                <div className="flex gap-1 px-3 pb-2">
                  {[1, 3, 12].map(months => (
                    <button
                      key={months}
                      onClick={() => handleGrantSubscription(months)}
                      disabled={loading === `sub-${months}`}
                      className="flex-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"
                    >
                      {loading === `sub-${months}` ? '...' : `${months}mo`}
                    </button>
                  ))}
                </div>

                <div className="border-t my-1" />

                {/* Referral Code */}
                {user.referralCode && (
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">Code: {user.referralCode}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={handleCopyCode}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Copy code"
                      >
                        <Copy className="h-3 w-3 text-gray-400" />
                      </button>
                      <button
                        onClick={handleRegenerateCode}
                        disabled={loading === 'referral'}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                        title="Regenerate code"
                      >
                        {loading === 'referral' ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Gift className="h-3 w-3 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                <div className="border-t my-1" />

                {/* Send Notification */}
                <button
                  onClick={handleSendNotification}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  <Bell className="h-4 w-4" />
                  Send Notification
                </button>

                {/* View Activity */}
                <button
                  onClick={handleViewActivity}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  <History className="h-4 w-4" />
                  View Activity
                </button>

                <div className="border-t my-1" />

                {/* Suspend/Unsuspend */}
                <button
                  onClick={handleToggleSuspend}
                  disabled={loading === 'suspend'}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded disabled:opacity-50 ${
                    isSuspended 
                      ? 'text-green-700 hover:bg-green-50' 
                      : 'text-orange-700 hover:bg-orange-50'
                  }`}
                >
                  {loading === 'suspend' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isSuspended ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <Ban className="h-4 w-4" />
                  )}
                  {isSuspended ? 'Unsuspend User' : 'Suspend User'}
                </button>

                {/* Delete */}
                <button
                  onClick={() => { onDelete(); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete User
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Render as icon buttons
  if (variant === 'icons') {
    return (
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" onClick={onEdit} title="Edit">
          <Edit className="h-4 w-4" />
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handleExtendTrial(7)} 
          title="Extend trial +7 days"
          disabled={loading === 'trial-7'}
        >
          <Clock className="h-4 w-4" />
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handleGrantSubscription(1)}
          className="text-green-600"
          title="Grant 1 month subscription"
          disabled={loading === 'sub-1'}
        >
          <Crown className="h-4 w-4" />
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleToggleSuspend}
          className={isSuspended ? 'text-green-600' : 'text-orange-600'}
          title={isSuspended ? 'Unsuspend' : 'Suspend'}
          disabled={loading === 'suspend'}
        >
          {isSuspended ? <CheckCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onDelete}
          className="text-red-600"
          title="Delete user"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Render as full buttons
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" size="sm" onClick={onEdit}>
        <Edit className="h-4 w-4 mr-1" /> Edit
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => handleExtendTrial(7)}
        disabled={loading === 'trial-7'}
      >
        <Clock className="h-4 w-4 mr-1" /> +7 Days
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => handleGrantSubscription(1)}
        className="text-green-600"
        disabled={loading === 'sub-1'}
      >
        <Crown className="h-4 w-4 mr-1" /> Grant Sub
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleToggleSuspend}
        className={isSuspended ? 'text-green-600' : 'text-orange-600'}
        disabled={loading === 'suspend'}
      >
        {isSuspended ? (
          <><CheckCircle className="h-4 w-4 mr-1" /> Unsuspend</>
        ) : (
          <><Ban className="h-4 w-4 mr-1" /> Suspend</>
        )}
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={onDelete}
        className="text-red-600"
      >
        <Trash2 className="h-4 w-4 mr-1" /> Delete
      </Button>
    </div>
  );
}
